import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useWhiteLabel, PoweredByBadge, InstituteHeader } from "@/contexts/WhiteLabelContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Mail, Lock, Shield, Loader2, CheckCircle2, Key } from "lucide-react";
import LegalConsentText from "@/components/LegalConsentText";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { API_BASE } from "@/services/api";
import { IndianHeritageBackdrop } from "@/components/IndianHeritageBackdrop";

// Helper to extract error message from API response
const getErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  if (!err.response?.data?.detail) return fallback;
  const detail = err.response.data.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || "Validation error").join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

/**
 * CLARIAS Authentication Page
 * 
 * Dual Auth Model:
 * - Email OTP (Primary & Default)
 * - Password (Optional Backup)
 */
const AuthPageOTP = () => {
  // Auth states
  const [step, setStep] = useState("email"); // email, otp, password-login
  const [loginMethod, setLoginMethod] = useState("otp"); // otp or password
  
  // Form data
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false); // Prevent double submission
  
  // Refs for OTP inputs
  const otpRefs = useRef([]);
  
  const { setAuth } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Continue with Google. Uses DIRECT Google OAuth when the app's own Google
  // client is configured (straight to Google's account chooser, "to continue to
  // clarias.in"); otherwise falls back to the zero-config Emergent-managed flow.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const continueWithGoogle = async () => {
    let mode = "emergent";
    let clientId = null;
    try {
      const { data } = await managedAxios.get(`${API_BASE}/auth/google/config`);
      mode = data.mode;
      clientId = data.client_id;
    } catch (_) { /* fall back to emergent */ }

    if (mode === "direct" && clientId) {
      const redirectUri = window.location.origin + "/auth/google";
      const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("google_oauth_state", state);
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        state,
        prompt: "select_account",
        access_type: "online",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
      return;
    }

    // Emergent-managed fallback
    const redirectUrl = window.location.origin + "/auth/google/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  // White-label context
  const { isWhiteLabel, config, subdomain, checkEmailWhitelisted, getInstituteName, getSupportPhone, shouldShowSignup } = useWhiteLabel();

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Focus first OTP input when step changes to otp
  useEffect(() => {
    if (step === "otp" && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [step]);

  // Capture referral code from ?ref=CODE so it can be attributed after signup/login
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) localStorage.setItem("clarias_ref", ref.trim().toUpperCase());
    } catch (e) { /* noop */ }
  }, []);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, "").slice(0, 6).split("");
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) newOtp[index + i] = digit;
      });
      setOtp(newOtp);
      // Focus last filled input or next empty
      const lastIndex = Math.min(index + digits.length - 1, 5);
      if (otpRefs.current[lastIndex]) {
        otpRefs.current[lastIndex].focus();
      }
      return;
    }
    
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5 && otpRefs.current[index + 1]) {
      otpRefs.current[index + 1].focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  // Request OTP
  const requestOTP = async () => {
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      // For white-label: Check if email is whitelisted first (client-side check)
      if (isWhiteLabel) {
        const whitelist = await checkEmailWhitelisted(email);
        if (!whitelist.allowed) {
          setError(whitelist.reason || "Email not registered with this institute.");
          if (whitelist.support_phone) {
            toast({
              title: "Contact Institute",
              description: `Call ${whitelist.support_phone} for access.`,
              variant: "destructive"
            });
          }
          setLoading(false);
          return;
        }
      }
      
      // Build URL with subdomain param for white-label enforcement
      const url = isWhiteLabel && subdomain 
        ? `${API_BASE}/auth/otp/request?subdomain=${subdomain}`
        : `${API_BASE}/auth/otp/request`;
      
      const response = await managedAxios.post(url, { email });
      const data = response.data;
      
      if (data.success) {
        setIsNewUser(data.is_new_user);
        setResendTimer(data.resend_available_in || 60);
        setStep("otp");
        toast({ description: "OTP sent to your email. Check your inbox." });
      } else {
        // Rate limited - still transition to OTP screen since an OTP was already sent
        if (data.resend_available_in && data.resend_available_in > 0) {
          setIsNewUser(data.is_new_user);
          setResendTimer(data.resend_available_in);
          setStep("otp"); // Allow user to enter OTP even if rate limited
          toast({ 
            description: "An OTP was already sent. Please check your inbox.",
            variant: "default"
          });
        } else {
          setError(data.message || "Failed to send OTP.");
        }
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to send OTP. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const verifyOTP = async () => {
    // Prevent double submission with separate flag
    if (loading || isVerifying) {
      console.log('Verify already in progress, skipping...');
      return;
    }
    
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }
    
    // If new user, name is required
    if (isNewUser && (!name.trim() || name.trim().length < 2)) {
      setError("Please enter your full name (at least 2 characters).");
      return;
    }
    
    setIsVerifying(true);
    setLoading(true);
    setError("");
    
    try {
      console.log('Verifying OTP:', { email, otp: otpString, name: isNewUser ? name : null });
      const response = await managedAxios.post(`${API_BASE}/auth/otp/verify`, {
        email,
        otp: otpString,
        name: isNewUser ? name.trim() : null
      });
      
      console.log('OTP Verify Response:', response.data);
      const data = response.data;
      
      // Store auth data
      localStorage.setItem("token", data.token.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setAuth({ user: data.user, token: data.token.access_token });
      
      // Show password setup prompt if needed
      if (data.show_password_prompt) {
        localStorage.setItem("show_password_prompt", "true");
      }
      
      toast({ 
        description: isNewUser 
          ? "Account created successfully! Welcome to CLARIAS." 
          : "Welcome back to CLARIAS." 
      });
      
      // Redirect to home which handles exam selection
      navigate("/");
    } catch (err) {
      console.error('OTP Verify Error:', err.response?.data || err.message);
      setError(getErrorMessage(err, "Invalid OTP. Please try again."));
      // Only reset OTP if we're on the OTP step, not name step
      if (step === "otp") {
        setOtp(["", "", "", "", "", ""]);
        if (otpRefs.current[0]) otpRefs.current[0].focus();
      }
    } finally {
      setLoading(false);
      setIsVerifying(false);
    }
  };

  // Password login
  const passwordLogin = async () => {
    if (!email || !password) {
      setError("Please enter email and password.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await managedAxios.post(`${API_BASE}/auth/password/login`, {
        email,
        password
      });
      
      const data = response.data;
      
      localStorage.setItem("token", data.token.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setAuth({ user: data.user, token: data.token.access_token });
      
      toast({ description: "Welcome back to CLARIAS." });
      // Redirect to home which handles exam selection
      navigate("/");
    } catch (err) {
      setError(getErrorMessage(err, "Invalid email or password."));
    } finally {
      setLoading(false);
    }
  };

  // White-label password login (for institute users)
  const handleWhitelabelPasswordLogin = async () => {
    if (!email || !password) {
      setError("Please enter email and password.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      // First check if email is whitelisted
      const whitelist = await checkEmailWhitelisted(email);
      if (!whitelist.allowed) {
        setError(whitelist.reason || "Email not registered with this institute.");
        if (whitelist.support_phone) {
          toast({
            title: "Contact Institute",
            description: `Call ${whitelist.support_phone} for access.`,
            variant: "destructive"
          });
        }
        setLoading(false);
        return;
      }
      
      // Login with password
      const url = `${API_BASE}/auth/password/login?subdomain=${subdomain}`;
      const response = await managedAxios.post(url, { email, password });
      
      const data = response.data;
      
      localStorage.setItem("token", data.token.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      
      // For white-label: Auto-select UPSC exam so they skip exam selector
      localStorage.setItem(`clarias_exam_${data.user.id}`, 'UPSC');
      localStorage.setItem(`clarias_exam_selector_v2_${data.user.id}`, 'true');
      
      setAuth({ user: data.user, token: data.token.access_token });
      
      toast({ description: `Welcome to ${getInstituteName()}!` });
      navigate("/dashboard"); // Go directly to dashboard for white-label users
    } catch (err) {
      const errorMsg = getErrorMessage(err, "Invalid email or password.");
      setError(errorMsg);
      
      // If user doesn't have password set, show helpful message
      if (errorMsg.includes("Password not set")) {
        setError("Your account doesn't have a password. Please contact your institute administrator.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    try {
      const response = await managedAxios.post(`${API_BASE}/auth/otp/resend`, { email });
      if (response.data.success) {
        setResendTimer(response.data.resend_available_in || 60);
        setOtp(["", "", "", "", "", ""]);
        toast({ description: "New OTP sent to your email." });
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to resend OTP."));
    } finally {
      setLoading(false);
    }
  };

  // Submit name for new user
  const submitName = () => {
    if (!name.trim() || name.trim().length < 2) {
      setError("Please enter your full name (at least 2 characters).");
      return;
    }
    // Now call verifyOTP with the name - skip the name check since we have it
    verifyOTP(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] via-[#0F172A] to-[#0B1220] flex flex-col safe-area-top relative overflow-hidden">
      {/* Continuity with PublicLandingPage hero — amber halo + subtle grid + heritage backdrop */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.30) 0%, rgba(245,158,11,0) 70%)' }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] pointer-events-none" />

      {/* V9 Hybrid — Indian heritage backdrop (matches landing hero) */}
      <IndianHeritageBackdrop />

      {/* Back */}
      <div className="p-4 sm:p-6 relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors bg-white/[0.04] border border-white/15 backdrop-blur-md rounded-full px-4 py-2 touch-target hover:bg-white/[0.08] click-scale"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-24 sm:pb-20 relative z-10">
        <div className="w-full max-w-md space-y-6 animate-fade-in-up">
          {/* Brand Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 bg-white/[0.03] border border-amber-400/40 backdrop-blur-md rounded-full px-4 py-2 shadow-sm">
              <Mail className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-300 font-medium">Secure email-based login</span>
            </div>

            {/* Show Institute Name for White-Label OR Clarias Logo */}
            {isWhiteLabel ? (
              <div className="flex flex-col items-center gap-2">
                {config?.logo_url ? (
                  <img
                    src={config.logo_url}
                    alt={getInstituteName()}
                    className="h-20 sm:h-24 object-contain"
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-4xl sm:text-5xl">{getInstituteName().charAt(0)}</span>
                  </div>
                )}
                <h1 className="text-3xl sm:text-4xl font-bold text-white mt-2">{getInstituteName()}</h1>
                <p className="text-slate-400 text-lg">UPSC Preparation Platform</p>
              </div>
            ) : (
              <div className="flex flex-col items-center pt-2">
                <span className="text-5xl sm:text-6xl font-serif font-bold text-white tracking-wide leading-none">Clarias</span>
                <span className="text-sm sm:text-base text-amber-400 font-medium tracking-wider mt-2">Clarity over Chaos</span>
              </div>
            )}
            <p className="text-slate-300/80 mt-4 text-lg tracking-wide">Prepare Less. Score More.</p>
          </div>

          {/* Auth Card — Dark glass on navy, orange-bordered active tab (matches reference) */}
          <Card className="relative bg-white/[0.025] border border-white/10 backdrop-blur-md shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)] rounded-2xl overflow-hidden">
            <CardContent className="p-6 sm:p-8 space-y-6">
              
              {/* Step 1: Email Entry */}
              {step === "email" && (
                <>
                  {/* For white-label institutes: Password login ONLY (no OTP, no signup) */}
                  {isWhiteLabel && !shouldShowSignup() ? (
                    <>
                      {/* White-label login notice */}
                      <div className="text-center py-2 px-4 bg-amber-50 rounded-xl border border-amber-200">
                        <p className="text-sm text-amber-700">
                          Login with your institute credentials
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-white">Email</Label>
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setError(""); }}
                            placeholder="your.email@institute.com"
                            className="h-12 bg-white/[0.03] border-white/15 text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-amber-400/30 rounded-xl"
                            data-testid="whitelabel-email-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-white">Password</Label>
                          <Input
                            type="password"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setError(""); }}
                            placeholder="Your password"
                            className="h-12 bg-white/[0.03] border-white/15 text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-amber-400/30 rounded-xl"
                            onKeyDown={(e) => e.key === "Enter" && handleWhitelabelPasswordLogin()}
                            data-testid="whitelabel-password-input"
                          />
                        </div>

                        {error && (
                          <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
                        )}

                        <Button
                          onClick={handleWhitelabelPasswordLogin}
                          disabled={loading}
                          className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-semibold shadow-lg shadow-amber-500/25 transition-all"
                          data-testid="whitelabel-login-button"
                        >
                          {loading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Logging in...</>
                          ) : (
                            <>Login <Lock className="w-4 h-4 ml-2" /></>
                          )}
                        </Button>
                      </div>

                      <p className="text-xs text-center text-slate-500">
                        Contact your institute if you don't have login credentials
                      </p>
                    </>
                  ) : (
                    <>
                      {/* Regular CLARIAS login with OTP/Password toggle */}
                      <div className="flex bg-white/[0.03] border border-white/10 rounded-full p-1.5">
                        <button
                          type="button"
                          onClick={() => setLoginMethod("otp")}
                          className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                            loginMethod === "otp"
                              ? "bg-transparent text-amber-400 ring-1 ring-amber-400/70 shadow-[0_0_18px_-4px_rgba(245,158,11,0.45)]"
                              : "text-slate-400 hover:text-slate-200"
                          }`}
                        >
                          <Mail className="w-4 h-4" />
                          Email OTP
                        </button>
                        <button
                          type="button"
                          onClick={() => setLoginMethod("password")}
                          className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                            loginMethod === "password"
                              ? "bg-transparent text-amber-400 ring-1 ring-amber-400/70 shadow-[0_0_18px_-4px_rgba(245,158,11,0.45)]"
                              : "text-slate-400 hover:text-slate-200"
                          }`}
                        >
                          <Lock className="w-4 h-4" />
                          Password
                        </button>
                      </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-white">Email</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(""); }}
                        placeholder="you@example.com"
                        className="h-12 bg-white/[0.03] border-white/15 text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-amber-400/30 rounded-xl"
                        onKeyDown={(e) => e.key === "Enter" && (loginMethod === "otp" ? requestOTP() : null)}
                      />
                    </div>

                    {loginMethod === "password" && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-white">Password</Label>
                        <Input
                          type="password"
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); setError(""); }}
                          placeholder="Your password"
                          className="h-12 bg-white/[0.03] border-white/15 text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-amber-400/30 rounded-xl"
                          onKeyDown={(e) => e.key === "Enter" && passwordLogin()}
                        />
                        <div className="text-right">
                          <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-slate-900">
                            Forgot password?
                          </Link>
                        </div>
                      </div>
                    )}

                    {error && (
                      <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
                    )}

                    <Button
                      onClick={loginMethod === "otp" ? requestOTP : passwordLogin}
                      disabled={loading}
                      className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-semibold shadow-lg shadow-amber-500/25 transition-all"
                    >
                      {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Please wait...</>
                      ) : loginMethod === "otp" ? (
                        <>Send OTP <Mail className="w-4 h-4 ml-2" /></>
                      ) : (
                        <>Login <Lock className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  </div>

                  {loginMethod === "otp" && (
                    <p className="text-xs text-center text-slate-500">
                      Login with Email OTP (recommended for quick access)
                    </p>
                  )}

                  {/* Divider + Continue with Google */}
                  <div className="flex items-center gap-3 my-1">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs text-slate-500 uppercase tracking-wider">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <button
                    type="button"
                    onClick={continueWithGoogle}
                    data-testid="continue-with-google-btn"
                    className="w-full h-12 flex items-center justify-center gap-3 bg-white text-slate-700 rounded-full font-semibold shadow-lg hover:bg-slate-50 transition-colors click-scale"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                    </svg>
                    Continue with Google
                  </button>
                    </>
                  )}
                </>
              )}

              {/* Step 2: OTP Verification */}
              {step === "otp" && (
                <>
                  <div className="text-center space-y-2">
                    <div className="w-14 h-14 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto">
                      <Key className="w-7 h-7 text-amber-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-white">Enter verification code</h2>
                    <p className="text-sm text-slate-400">
                      We sent a 6-digit code to<br />
                      <span className="font-medium text-slate-200">{email}</span>
                    </p>
                  </div>

                  {/* OTP Input */}
                  <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-12 h-14 text-center text-xl font-bold border-2 border-white/15 bg-white/[0.03] text-white rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-400/30 outline-none transition-all"
                      />
                    ))}
                  </div>

                  {/* Name field for new users */}
                  {isNewUser && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-white">Full Name</Label>
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => { setName(e.target.value); setError(""); }}
                        placeholder="Enter your full name"
                        className="h-12 bg-white/[0.03] border-white/15 text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-amber-400/30 rounded-xl"
                      />
                    </div>
                  )}

                  {error && (
                    <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg text-center">{error}</p>
                  )}

                  <Button
                    onClick={() => verifyOTP()}
                    disabled={loading || otp.join("").length !== 6 || (isNewUser && name.trim().length < 2)}
                    className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full font-semibold shadow-lg shadow-amber-500/25 transition-all"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                    ) : isNewUser ? (
                      <>Create Account</>
                    ) : (
                      <>Verify & Continue <CheckCircle2 className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>

                  {/* Resend */}
                  <div className="text-center">
                    {resendTimer > 0 ? (
                      <p className="text-sm text-slate-500">
                        Resend OTP in <span className="font-medium text-slate-700">{resendTimer}s</span>
                      </p>
                    ) : (
                      <button
                        onClick={resendOTP}
                        disabled={loading}
                        className="text-sm text-amber-400 hover:text-amber-300 font-medium"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>

                  {/* Change email */}
                  <button
                    onClick={() => { setStep("email"); setOtp(["", "", "", "", "", ""]); setError(""); }}
                    className="w-full text-sm text-slate-500 hover:text-slate-700"
                  >
                    ← Use a different email
                  </button>
                </>
              )}

              {/* Step 3: Name for New Users */}

              {/* Legal Consent */}
              <LegalConsentText variant="auth" className="mt-4" />
            </CardContent>
          </Card>

          {/* Powered by CLARIAS - For White-Label */}
          {isWhiteLabel && (
            <div className="text-center py-3 px-4 bg-slate-100/50 rounded-xl border border-slate-200">
              <p className="text-sm text-slate-500">
                Powered by <span className="font-semibold text-amber-400">CLARIAS Intelligence</span>
              </p>
            </div>
          )}

          {/* Trust Indicators */}
          <div className="flex justify-center gap-6 text-xs text-slate-300">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-amber-400" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-amber-400" />
              <span>Email-only</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-amber-400" />
              <span>No SMS</span>
            </div>
          </div>

          {/* Trust Copy */}
          <p className="text-center text-xs text-slate-400 px-4">
            {isWhiteLabel
              ? `${getInstituteName()} uses secure email-based login to protect your preparation history.`
              : "CLARIAS uses secure email-based login to protect your preparation history and subscriptions."
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPageOTP;
